import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MaterialReactTable,
  useMaterialReactTable,
  createMRTColumnHelper,
} from 'material-react-table';
import {
  Box,
  Button,
  createTheme,
  ThemeProvider,
  useTheme,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import Modal from 'react-modal';
import { getReportVehicles } from '../../actions/apiGetInfo';
import { returnDaysHours, readableStampSimpleMonthYear } from '../../utils/dateFunctions';
import ReportVehiclesModal from './ReportVehiclesModal';
import NestedHistoryTable from './NestedHistoryTable';
import GroupAveragesChart from './GroupAveragesChart';
import {
  formatDaysHours,
  getTotalMinutes,
  getLastSixMonthsReportTypes,
  computeAverageTime,
  departmentAliasLabel,
} from './chartUtils';
import _ from 'lodash';

const TableDepartments = ({ data, dealership_report_hours, aliasView = false }) => {
  const globalTheme = useTheme();
  const dispatch = useDispatch();
  const {
    report_vehicles,
    report_department_data,
    installation,
    report_dealerships,
    departments,
    related_installations,
  } = useSelector((state) => ({
    report_vehicles: state.settings_reports.report_vehicles,
    report_department_data: state.settings_reports.report_department_data,
    installation: state.settings_dealerships.installation,
    report_dealerships: state.settings_reports.report_dealerships,
    departments: state.settings_departments.original_departments,
    related_installations: state.settings_dealerships.related_installations,
  }));

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState({ report_id: 0, department_id: 0, dealership: '' });
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(null);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  useEffect(() => {
    if (loadingVehicles && report_vehicles.length > 0) {
      setLoadingVehicles(false);
      setModalIsOpen(true);
    }
  }, [report_vehicles, loadingVehicles]);

  const openModal = (report_id, department_id) => {
    if (report_id > 0) {
      setLoadingVehicles(true);
      setSelectedReport({ report_id, department_id, dealership: '' });
      dispatch(getReportVehicles(report_id)).catch((error) => {
        // console.error('Error fetching report vehicles:', error);
        setLoadingVehicles(false);
      });
    }
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setLoadingVehicles(false);
  };

  const openGroupModal = (range, department_id, dealership) => {
    if (!range || !(['7', '14', '30', '90'].includes(range) || /^[0-9]{4}_[0-9]{2}$/.test(range))) {
      // console.warn(`Invalid range for opening modal:`, { range });
      return;
    }
    // console.log(`Opening group modal with range: ${range}, department_id: ${department_id}, dealership: ${dealership}, hours setting: ${dealership_report_hours}`);
    setSelectedReport(prev => ({ ...prev, department_id, dealership }));
    setSelectedRange(range);
    setGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    // console.log(`Closing group modal, resetting selectedRange`);
    setGroupModalOpen(false);
    setSelectedRange(null);
  };

  const tableTheme = useMemo(() => createTheme({
    palette: {
      mode: 'light',
    },
  }), [globalTheme]);

  const columnHelper = createMRTColumnHelper();

  const getDepartmentName = (id) => {
    const dept = departments.find(d => parseInt(d.id) === parseInt(id));
    return dept ? (dept.display_name || dept.name || 'Unknown') : 'Unknown';
  };

  const memberDepartmentIds = (aliasLabel, dealership) => {
    if (!aliasView) return [aliasLabel];
    return departments
      .filter(d => departmentAliasLabel(d) === aliasLabel
        && (d.dealerships || '').split(',').map(s => s.trim()).includes(dealership))
      .map(d => d.id);
  };

  const reportRowIndex = useMemo(() => {
    const index = new Map();
    (report_department_data || []).forEach((item) => {
      if (!item) return;
      index.set(`${item.dealership}|${item.department_id}|${(item.report_type || '').trim().toLowerCase()}`, item);
    });
    return index;
  }, [report_department_data]);

  const uniqueDealerships = useMemo(
    () => [...new Set((report_department_data || []).map(item => item?.dealership).filter(Boolean))],
    [report_department_data],
  );

  const groupDealerships = useMemo(() => {
    const selected = (report_dealerships || '').split(',').map(s => s.trim()).filter(Boolean);
    return selected.length ? selected : uniqueDealerships;
  }, [report_dealerships, uniqueDealerships]);

  const aggregateRange = (aliasLabel, dealership, range) => {
    const members = memberDepartmentIds(aliasLabel, dealership);
    let dealerTime = 0;
    let dealerCount = 0;
    const memberReportIds = [];
    members.forEach((mid) => {
      const entry = reportRowIndex.get(`${dealership}|${mid}|step-${range}`);
      const count = entry ? Number(entry.vehicle_count) || 0 : 0;
      if (entry && count > 0) {
        dealerTime += (Number(computeAverageTime(entry, dealership_report_hours)) || 0) * count;
        dealerCount += count;
        if (entry.id) memberReportIds.push(entry.id);
      }
    });
    let groupTime = 0;
    let groupCount = 0;
    groupDealerships.forEach((dl) => {
      members.forEach((mid) => {
        const entry = reportRowIndex.get(`${dl}|${mid}|step-${range}`);
        const count = entry ? Number(entry.vehicle_count) || 0 : 0;
        if (entry && count > 0) {
          groupTime += (Number(computeAverageTime(entry, dealership_report_hours)) || 0) * count;
          groupCount += count;
        }
      });
    });
    return {
      deptAvg: dealerCount > 0 ? dealerTime / dealerCount : 0,
      vehicleCount: dealerCount,
      groupAverage: groupCount > 0 ? groupTime / groupCount : 0,
      memberReportIds,
      memberDeptIds: members,
    };
  };

  const buildAliasReportRows = (aliasLabel) => {
    const ranges = ['7', '14', '30', '90'];
    const rows = [];
    groupDealerships.forEach((dealership) => {
      const members = memberDepartmentIds(aliasLabel, dealership);
      ranges.forEach((range) => {
        let w5 = 0;
        let w6 = 0;
        let w7 = 0;
        let count = 0;
        members.forEach((mid) => {
          const entry = reportRowIndex.get(`${dealership}|${mid}|step-${range}`);
          const c = entry ? Number(entry.vehicle_count) || 0 : 0;
          if (entry && c > 0) {
            w5 += (Number(entry.rawtime_24_5) || 0) * c;
            w6 += (Number(entry.rawtime_24_6) || 0) * c;
            w7 += (Number(entry.rawtime_24_7) || 0) * c;
            count += c;
          }
        });
        if (count > 0) {
          rows.push({
            dealership,
            department_id: aliasLabel,
            department_type: 'department',
            report_type: `step-${range}`,
            rawtime_24_5: w5 / count,
            rawtime_24_6: w6 / count,
            rawtime_24_7: w7 / count,
            vehicle_count: count,
          });
        }
      });
    });
    return rows;
  };

  const columns = [
    columnHelper.accessor('dealership', {
      header: 'Dealership',
      size: 120,
    }),
    columnHelper.accessor('department', {
      header: aliasView ? 'Alias' : 'Department',
      accessorFn: (row) => (aliasView ? (row.department || 'Unknown') : getDepartmentName(row.department_id)),
    }),
    columnHelper.accessor('average_time', {
      header: 'Avg Time',
      Cell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
            {row.original.average_time_dhm || 'N/A'}
          </span>
        </Box>
      ),
      sortingFn: (rowA, rowB) => {
        const timeA = rowA.original.average_time_minutes || 0;
        const timeB = rowB.original.average_time_minutes || 0;
        return timeA - timeB;
      },
    }),
    columnHelper.accessor('number_of_vehicles', {
      header: 'Vehicle Count',
      Cell: ({ row }) => (
        <Typography
          sx={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}
        >
          {row.original.number_of_vehicles || 0}
        </Typography>
      ),
    }),
    columnHelper.accessor('goal', {
      header: 'Goal',
      Cell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
            {Number(row.original.goal) > 0 ? returnDaysHours(Number(row.original.goal)) : ''}
          </span>
        </Box>
      ),
    }),
    columnHelper.accessor('details', {
      header: 'Details',
      Cell: ({ row }) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          {!aliasView && (
            <ReportVehiclesModal department_id={row.original.department_id} report_id={row.original.report_id} />
          )}
        </Box>
      ),
    }),
  ];

  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const year = now.getFullYear();
  const filename = `departments_report_${month}_${day}_${year}`;

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename,
  });

  const exportableColumnKeys = ['dealership', 'department', 'average_time_dhm', 'number_of_vehicles', 'goal'];

  const reportDealerships = useMemo(() => {
    // console.log('Debugging reportDealerships:', { report_dealerships });
    return report_dealerships ? report_dealerships.split(',').filter(d => d.trim()) : Object.keys(data.yAxis || {});
  }, [report_dealerships, data]);

  const tableData = useMemo(() => {
    // console.log('Debugging tableData inputs:', { data, reportDealerships, hasXAxis: !!data?.xAxis, isXAxisArray: Array.isArray(data?.xAxis) });
    if (!data || !reportDealerships.length || !data.xAxis || !Array.isArray(data.xAxis)) {
      // console.warn('Invalid table data inputs:', { hasData: !!data, hasDealerships: reportDealerships.length > 0, hasXAxis: !!data?.xAxis, isXAxisArray: Array.isArray(data?.xAxis) });
      return [];
    }

    return reportDealerships.flatMap((dealership) => {
      if (!data.yAxis?.[dealership]) {
        // console.warn(`No yAxis data for dealership: ${dealership}`);
        return [];
      }
      return data.xAxis.map((department, index) => {
        const number_of_vehicles = data.xCount?.[dealership]?.[index] || 0;
        if (number_of_vehicles > 0) {
          const averageTime = data.yAxis?.[dealership]?.[index] || 0;
          return {
            dealership,
            department: department || '',
            average_time: averageTime,
            average_time_dhm: formatDaysHours(averageTime),
            average_time_minutes: getTotalMinutes(averageTime),
            average_time_24_5: data.rawtime_24_5?.[dealership]?.[index] || '',
            average_time_24_6: data.rawtime_24_6?.[dealership]?.[index] || '',
            report_id: data.dataId?.[dealership]?.[index] || '',
            department_id: data.departmentId?.[dealership]?.[index] || '',
            goal: data.goal?.[dealership]?.[index] || '',
            report_month: data.xAxis[index] || '',
            number_of_vehicles,
            vehicles: [],
          };
        }
        return null;
      }).filter(item => item !== null);
    });
  }, [data, reportDealerships, dealership_report_hours]);

  const handleExportToCSV = () => {
    const rowData = tableData.map(item => exportableColumnKeys.reduce((acc, key) => {
      acc[key] = key === 'department' ? (aliasView ? item.department : getDepartmentName(item.department_id)) :
                key === 'goal' && Number(item.goal) > 0 ? returnDaysHours(Number(item.goal)) :
                item[key] || '';
      return acc;
    }, {}));
    const csv = generateCsv(csvConfig)(rowData);
    download(csvConfig)(csv);
  };

  const handleExportToPDFwGraph = () => {
    const doc = new jsPDF();
    const tableColumn = ['Dealership', 'Departments', 'Average Time', 'Number of Vehicles', 'Goal'];
    const tableRows = tableData.map(item => [
      item.dealership,
      aliasView ? item.department : getDepartmentName(item.department_id),
      item.average_time_dhm || 'N/A',
      item.number_of_vehicles,
      Number(item.goal) > 0 ? returnDaysHours(Number(item.goal)) : '',
    ]);

    const summaryElement = document.getElementById('report-summary');
    const summaryText = summaryElement ? summaryElement.textContent : '';

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const maxTextWidth = pageWidth - margin * 2;

    const wrappedText = doc.splitTextToSize(summaryText, maxTextWidth);

    doc.setFontSize(12);
    doc.text(wrappedText, margin, 10);

    const textHeight = wrappedText.length * 15;
    let startYAfterText = textHeight + 10;

    const possibleChartIds = ['chart', 'monthly-chart', 'group-chart'];
    let element = null;
    for (const id of possibleChartIds) {
      element = document.getElementById(id);
      if (element) break;
    }

    if (element) {
      html2canvas(element, { scale: 2 }).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const imgProps = doc.getImageProperties(imgData);
        const pdfWidth = pageWidth - margin * 2;
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        doc.addImage(imgData, 'PNG', margin, startYAfterText, pdfWidth, pdfHeight);

        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: startYAfterText + pdfHeight + 10,
        });
        doc.save(filename);
      }).catch(error => {
        // console.error('Failed to capture chart:', error);
        autoTable(doc, {
          head: [tableColumn],
          body: tableRows,
          startY: startYAfterText + 10,
        });
        doc.save(filename);
      });
    } else {
      // console.warn('Chart element not found. Exporting PDF without graph.');
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: startYAfterText + 10,
      });
      doc.save(filename);
    }
  };

  const handleExportToPDF = () => {
    const doc = new jsPDF();
    const tableColumn = ['Dealership', 'Departments', 'Average Time', 'Number of Vehicles', 'Goal'];
    const tableRows = tableData.map(item => [
      item.dealership,
      aliasView ? item.department : getDepartmentName(item.department_id),
      item.average_time_dhm || 'N/A',
      item.number_of_vehicles,
      Number(item.goal) > 0 ? returnDaysHours(Number(item.goal)) : '',
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 10,
    });
    doc.save(filename);
  };

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    enableRowSelection: false,
    enableExpanding: true,
    enablePagination: false,
    positionToolbarAlertBanner: 'bottom',
    initialState: { showColumnFilters: false, expanded: false },
    renderDetailPanel: ({ row }) => {
      const isExpanded = row.getIsExpanded();

      const lastSixMonths = getLastSixMonthsReportTypes();
      const nestedData = isExpanded ? (report_department_data || [])
        .filter(item =>
          item && item.dealership === row.original.dealership && item.department_id === row.original.department_id &&
          lastSixMonths.includes(item.report_type?.trim().toLowerCase()) &&
          item.department_type === 'department'
        )
        .map(item => {
          const average_time = computeAverageTime(item, dealership_report_hours);
          return {
            dealership: item.dealership || '',
            department: getDepartmentName(item.department_id),
            report_month: readableStampSimpleMonthYear(item.recon_end_date) || '',
            average_time,
            average_time_dhm: formatDaysHours(average_time) || 'N/A',
            average_time_minutes: getTotalMinutes(average_time) || 0,
            number_of_vehicles: Number(item.vehicle_count) || 0,
            goal: item.goal || '',
            report_id: item.id || '',
            department_id: item.department_id || '',
          };
        }) : [];
      // console.log(`Nested data count for department ${row.original.department_id}:`, nestedData.length, { nestedData });

      const timeRanges = ['7', '14', '30', '90'];
      const metricsRef = React.useRef(null);

      const handleExportDetailToPDF = () => {
        const doc = new jsPDF();
        const now = new Date();
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const day = now.getDate().toString().padStart(2, '0');
        const year = now.getFullYear();
        const departmentName = departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.name || 'Unknown';
        const filename = `department_summary_${month}_${day}_${year}.pdf`;

        // Add title
        doc.setFontSize(16);
        doc.text(`${departmentName} Summary`, 10, 10);

        // Capture metrics boxes
        if (metricsRef.current) {
          html2canvas(metricsRef.current).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const imgProps = doc.getImageProperties(imgData);
            const pdfWidth = doc.internal.pageSize.getWidth() - 20;
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            doc.addImage(imgData, 'PNG', 10, 20, pdfWidth, pdfHeight);

            // Add nested table
            if (nestedData.length > 0) {
              const tableColumn = ['Dealership', 'Department', 'Month', 'Avg Time', 'Vehicle Count'];
              const tableRows = nestedData.map(item => [
                item.dealership,
                item.department,
                item.report_month,
                item.average_time_dhm,
                item.number_of_vehicles,
              ]);
              autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 20 + pdfHeight + 10,
                styles: { fontSize: 10 },
              });
            }

            doc.save(filename);
          }).catch(error => {
            // console.error('Error exporting detail panel to PDF:', error);
          });
        } else {
          // console.warn('Metrics container not found for PDF export');
        }
      };

      const metricsHeader = isExpanded ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '8px' }}>
            <Button
              onClick={handleExportDetailToPDF}
              startIcon={<FileDownloadIcon />}
              variant="contained"
              color="primary"
              size="small"
            >
              Export to PDF
            </Button>
          </Box>
          <Box ref={metricsRef} sx={{ padding: '8px', backgroundColor: '#f5f5f5', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, color: '#007bff' }}>
              {`${aliasView ? row.original.department_id : (departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.display_name || departments.find(d => parseInt(d.id) === parseInt(row.original.department_id))?.name || 'Unknown')} Summary`}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: '8px', flexWrap: 'wrap' }}>
              {timeRanges.map((range, index) => {
                const {
                  deptAvg,
                  vehicleCount,
                  groupAverage,
                  memberReportIds,
                  memberDeptIds,
                } = aggregateRange(row.original.department_id, row.original.dealership, range);
                const reportId = memberReportIds[0] || 0;
                return (
                  <Box
                    key={range}
                    sx={{
                      flex: '1 1 22%',
                      minWidth: '200px',
                      padding: '12px',
                      border: '2px solid #ccc',
                      borderRadius: '8px',
                      backgroundColor: index % 2 === 0 ? '#f8f9fa' : '#e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#dee2e6',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333' }}>
                      {`${range}-Day Avg`}
                    </Typography>
                    <Typography sx={{ color: deptAvg ? '#28a745' : '#dc3545' }}>
                      {deptAvg ? formatDaysHours(deptAvg) : 'N/A'}
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.9rem', color: '#555', cursor: 'pointer' }}
                    >
                      Vehicles: {vehicleCount || 0}
                      {' '}
                      {aliasView ? (
                        <ReportVehiclesModal department_id={row.original.department_id} report_id={0} reportIds={memberReportIds} departmentIds={memberDeptIds} />
                      ) : (
                        <ReportVehiclesModal department_id={row.original.department_id} report_id={reportId} />
                      )}
                    </Typography>
                    {related_installations && related_installations.length > 1 && (
                      <div
                        onClick={() => openGroupModal(range, row.original.department_id, row.original.dealership)}
                        className="btn btn-large btn-success btn-block space-top-1 text-center"
                      >
                        Group Avg: {groupAverage ? formatDaysHours(groupAverage) : 'N/A'}
                      </div>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      ) : null;

      const nestedColumns = [
        columnHelper.accessor('dealership', { header: 'Dealership', size: 120 }),
        columnHelper.accessor('department', { header: 'Department' }),
        columnHelper.accessor('report_month', { header: 'Month', size: 100 }),
        columnHelper.accessor('average_time', {
          header: 'Avg Time',
          Cell: ({ row }) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
                {row.original.average_time_dhm}
              </span>
            </Box>
          ),
          sortingFn: (rowA, rowB) => rowA.original.average_time_minutes - rowB.original.average_time_minutes,
        }),
        columnHelper.accessor('number_of_vehicles', { header: 'Vehicle Count' }),
        columnHelper.accessor('goal', {
          header: 'Goal',
          Cell: ({ row }) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <span className={row.original.average_time > (Number(row.original.goal) / 24) ? 'text-danger' : 'text-success'}>
                {Number(row.original.goal) > 0 ? returnDaysHours(Number(row.original.goal)) : ''}
              </span>
            </Box>
          ),
        }),
        columnHelper.accessor('details', {
          header: 'Details',
          Cell: ({ row }) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <ReportVehiclesModal department_id={row.original.department_id} report_id={row.original.report_id} />
            </Box>
          ),
        }),
      ];

      if (!isExpanded) return <div />;

      return (
        <Box sx={{ backgroundColor: '#f5f5f5', padding: '16px' }}>
          {metricsHeader}
          {aliasView ? (
            <Typography sx={{ textAlign: 'center', color: '#888', padding: '12px' }}>
              Monthly breakdown by alias is coming soon.
            </Typography>
          ) : (
            <NestedHistoryTable columns={nestedColumns} data={nestedData} />
          )}
        </Box>
      );
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', gap: '16px', padding: '8px', flexWrap: 'wrap' }}>
        <Button onClick={handleExportToCSV} startIcon={<FileDownloadIcon />}>
          Export to CSV
        </Button>
        <Button onClick={handleExportToPDF} startIcon={<FileDownloadIcon />}>
          Export to PDF
        </Button>
        <Button onClick={handleExportToPDFwGraph} startIcon={<FileDownloadIcon />}>
          Export to PDF (Include Graph)
        </Button>
      </Box>
    ),
  });

  const total_vehicles = useMemo(() => {
    return tableData.reduce((sum, item) => sum + (item.number_of_vehicles > 0 ? item.number_of_vehicles : 0), 0);
  }, [tableData]);

  const total_times = useMemo(() => {
    return tableData.reduce((sum, item) => sum + (item.number_of_vehicles > 0 ? item.number_of_vehicles * (item.average_time || 0) : 0), 0);
  }, [tableData]);

  const total_average_times = useMemo(() => {
    const validItems = tableData.filter(item => item.number_of_vehicles > 0 && item.average_time);
    return validItems.length > 0 ? total_times / total_vehicles : 0;
  }, [total_times, total_vehicles]);

  const range = useMemo(() => {
    switch (dealership_report_hours) {
      case 'age-24_7': return '(24/7)';
      case 'age-24_6': return '(24/6)';
      case 'age-24_5': return '(24/5)';
      default: return '(24/7)';
    }
  }, [dealership_report_hours]);

  return (
    <ThemeProvider theme={tableTheme}>
      <MaterialReactTable table={table} />
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Report Vehicles"
        className="customModal"
        ariaHideApp={false}
        overlayClassName="overlay"
      >
        <div className="customCard">
          <div className="customHeader">
            <button
              type="button"
              className="btn btn-danger btn-sm float-end space-left-2"
              aria-label="Close"
              onClick={closeModal}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Typography variant="h5">Vehicle Report</Typography>
          </div>
          <ReportVehiclesModal department_id={selectedReport.department_id} report_id={selectedReport.report_id} />
        </div>
      </Modal>
      <Modal
        isOpen={groupModalOpen}
        onRequestClose={closeGroupModal}
        contentLabel="Group Averages"
        className="customModal"
        ariaHideApp={false}
        overlayClassName="overlay"
      >
        <div className="customCard" style={{ width: '50%', margin: '0 auto' }}>
          <div className="customHeader" style={{ textAlign: 'center' }}>
            <button
              type="button"
              className="btn btn-danger btn-sm float-end space-left-2"
              aria-label="Close"
              onClick={closeGroupModal}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Typography variant="h5">{selectedRange ? `${selectedRange}-day Group Averages` : 'Loading...'}</Typography>
          </div>
          <Box sx={{ padding: '16px', textAlign: 'center' }}>
            <hr style={{ margin: '8px 0', width: '100%', border: '1px solid #ccc' }} />
            {selectedRange && report_department_data ? (
              <GroupAveragesChart
                range={selectedRange}
                reportDepartmentData={aliasView ? buildAliasReportRows(selectedReport.department_id) : report_department_data.filter(r => groupDealerships.includes(r?.dealership))}
                dealershipReportHours={dealership_report_hours}
                department_id={selectedReport.department_id}
                departmentName={aliasView ? selectedReport.department_id : undefined}
              />
            ) : (
              <Typography>Loading data...</Typography>
            )}
          </Box>
        </div>
      </Modal>
    </ThemeProvider>
  );
};

export default React.memo(TableDepartments);